import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";

const CreateOrganizationDialog = ({ open, onSubmit, loading }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: "",
    GST_registration_no: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Organization name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        name: formData.name.trim(),
        GST_registration_no: formData.GST_registration_no.trim() || null,
        description: formData.description.trim() || null,
      });
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth='sm'
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle sx={{ pt: 4, pb: 2, px: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessIcon sx={{ fontSize: 28 }} />
          </Box>
          <Typography variant='h5' sx={{ fontWeight: 700 }}>
            Create Organization
          </Typography>
        </Box>
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Please set up your organization to continue using the application.
          This information helps us personalize your experience.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
          <TextField
            label='Organization Name'
            value={formData.name}
            onChange={handleChange("name")}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
            fullWidth
            placeholder='Enter your organization name'
            disabled={loading}
            autoFocus
          />

          <TextField
            label='GST Registration Number'
            value={formData.GST_registration_no}
            onChange={handleChange("GST_registration_no")}
            fullWidth
            placeholder='e.g., 201234567X (Optional)'
            disabled={loading}
          />

          <TextField
            label='Description'
            value={formData.description}
            onChange={handleChange("description")}
            fullWidth
            multiline
            rows={3}
            placeholder='Brief description of your organization (Optional)'
            disabled={loading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 2 }}>
        <Button
          onClick={handleSubmit}
          variant='contained'
          color='primary'
          fullWidth
          disabled={loading}
          sx={{
            py: 1.5,
            borderRadius: 1.5,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
              backgroundColor: "primary.dark",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color='inherit' />
          ) : (
            "Create Organization"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrganizationDialog;
